import MainTitle from "../../../components/ui/MainTitle/MainTitle";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetEmailTemplateSubjectsQuery,
  useGetEmailTemplateVersionByLanguageQuery,
} from "../../../api/queries";
import {
  EmailTemplateLanguageType,
  EmailTemplateStatusEnum,
  ICreateOrUpdateEmailTemplate,
} from "../../../models/EmailTemplate";
import { Form, FormInstance } from "antd";
import FormItemSelect from "../../../components/ui/FormItemSelect/FormItemSelect";
import RichTextEditor from "../../../components/ui/RichTextEditor/RichTextEditor";
import CustomButton from "../../../components/ui/CustomButton/CustomButton";
import { openErrorMessage, openSuccessMessage } from "../../../store/message/messageSlice";
import useAppDispatch from "../../../hooks/useAppDispatch";
import { useEmailTemplateMutations } from "../../../api/mutations/useEmailTemplateMutations";
import { handleError } from "../../../helper/errorHandler";
import AutomatedEmailsConstants from "../AutomatedEmailsConstants/AutomatedEmailsConstants";
import { convertQuillToInlineStyles } from "../../../helper/convertQuillToInlineStyles";

const languageSelectItems: { id: EmailTemplateLanguageType; label: string }[] = [
  { id: "en", label: "English" },
  { id: "gr", label: "Greek" },
];

const CreateAutomatedEmail = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { id } = useParams();
  const formRef = useRef<FormInstance>(null);
  const { t, i18n } = useTranslation();
  const { createOrUpdateEmailTemplate } = useEmailTemplateMutations();

  const defaultLanguage = languageSelectItems[0].id;
  const [selectedLanguage, setSelectedLanguage] = useState(defaultLanguage);
  const [content, setContent] = useState("");

  const { data: emailContractVersion = null } = useGetEmailTemplateVersionByLanguageQuery(
    id!,
    selectedLanguage
  );
  const { data: subjects = [] } = useGetEmailTemplateSubjectsQuery();

  const subjectSelectItems = useMemo(() => {
    return subjects.map((el) => {
      const label = i18n.language === "en" ? el.labelEn : el.labelGr;
      return {
        id: el.id,
        label,
        disabled: el.alreadyInUse,
      };
    });
  }, [i18n.language, subjects]);
  const statusSelectItems = useMemo(() => {
    return [
      { id: EmailTemplateStatusEnum.Active, label: t("Active") },
      { id: EmailTemplateStatusEnum.Inactive, label: t("Inactive") },
      { id: EmailTemplateStatusEnum.Draft, label: t("Draft") },
    ];
  }, [t]);

  useEffect(() => {
    if (id !== "new" && emailContractVersion && formRef.current) {
      formRef.current.setFieldsValue({
        subject: emailContractVersion.emailTemplate.subject,
        status: emailContractVersion.emailTemplate.status,
        language: emailContractVersion.language,
      });
      setSelectedLanguage(emailContractVersion.language);
      setContent(convertQuillToInlineStyles(emailContractVersion.content));
    }
  }, [emailContractVersion, id]);

  const onFinish = async (values: Omit<ICreateOrUpdateEmailTemplate, "content">) => {
    if (!content) {
      dispatch(openErrorMessage(t("ContentCanNotBeEmpty")));
      return;
    }

    const model: ICreateOrUpdateEmailTemplate = {
      content: convertQuillToInlineStyles(content),
      ...values,
    };

    createOrUpdateEmailTemplate(model, {
      onSuccess: () => {
        dispatch(openSuccessMessage(t("EmailTemplateSavedSuccessfully")));
        navigate("/automated-emails");
      },
      onError: (e) => handleError(e, dispatch, t),
    });
  };

  return (
    <div className={"page-content"}>
      <MainTitle text={t("AutomatedEmails")} />

      <Form
        className={"form"}
        ref={formRef}
        name="create-email-template"
        onFinish={onFinish}
        autoComplete="off"
        layout={"vertical"}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "24px" }}>
          <FormItemSelect
            name={"subject"}
            label={t("EmailSubject")}
            rules={{ required: true }}
            items={subjectSelectItems}
            disabled={!!emailContractVersion}
          />

          <FormItemSelect
            name={"language"}
            label={t("Language")}
            rules={{ required: true }}
            items={languageSelectItems}
            onChange={(value) => {
              const typeValue = value as EmailTemplateLanguageType;
              setSelectedLanguage(typeValue);
            }}
          />

          <FormItemSelect
            name={"status"}
            label={t("Status")}
            rules={{ required: true }}
            items={statusSelectItems}
          />
        </div>

        <RichTextEditor
          value={content}
          setValue={setContent}
          style={{ width: "100%", height: "570px" }}
        />

        <CustomButton
          text={t("SaveChanges")}
          type={"button"}
          typeStyle={"fill"}
          style={{ width: "400px", marginTop: "65px" }}
          functionOnClick={() => formRef.current?.submit()}
        />
      </Form>

      <AutomatedEmailsConstants />
    </div>
  );
};

export default CreateAutomatedEmail;
